import { handleBreakpoints, type ResponsiveStyleValue } from '@mui/system';
import { pxToRem } from '@packages/themes/utils';
import { type } from '../../../../theme';
import { makeStyles } from '../../styles/tss-react';
import { TextFieldSize } from './types';

export const ADORNMENT_ICON_SIZE_PX = 24;
export const ADORNMENT_GAP_PX = 8;
export const FIELD_INLINE_PADDING_PX = 16;

const FIELD = '[data-text-field]';

export interface TextFieldStyleProps {
  fullWidth: boolean;
  inputSize: ResponsiveStyleValue<TextFieldSize>;
  multiline: boolean;
}

function getInputBlockPadding(size: TextFieldSize, px: typeof pxToRem) {
  const block = size === TextFieldSize.Small ? 8 : 16;
  return {
    paddingTop: px(block),
    paddingBottom: px(block),
  };
}

function getInputTypography(size: TextFieldSize) {
  return {
    ...(size === TextFieldSize.Small ? type['16_regular'] : type['18_regular']),
    lineHeight: pxToRem(26),
  };
}

function getMultilineMinHeight(size: TextFieldSize, px: typeof pxToRem) {
  return {
    height: 'auto',
    minHeight: size === TextFieldSize.Small ? px(88) : px(120),
  };
}

export const useStyles = makeStyles<TextFieldStyleProps, 'helper'>({
  name: 'TextField',
  uniqId: 'TextField',
})(
  (
    { palette, transitions, breakpoints },
    { fullWidth, inputSize, multiline },
    classes
  ) => {
    const { input: inputTokens } = palette.semantic;
    const settled = `&:not(:has(${FIELD}:disabled)):not(:has(${FIELD}[readonly])):not(:has(${FIELD}[aria-invalid="true"]))`;

    return {
      root: {
        minWidth: 0,
        ...(fullWidth ? { width: '100%' } : {}),
        [`&:has(${FIELD}:disabled)`]: {
          opacity: 0.5,
        },
        [`&:has(${FIELD}[aria-invalid="true"]) .${classes.helper}`]: {
          color: palette.semantic.text.error,
        },
      },
      control: {
        display: fullWidth ? 'flex' : 'inline-flex',
        alignItems: multiline ? 'flex-start' : 'center',
        gap: pxToRem(ADORNMENT_GAP_PX),
        boxSizing: 'border-box',
        ...(fullWidth ? { width: '100%' } : {}),
        borderRadius: pxToRem(8),
        backgroundColor: inputTokens.bgPrimary,
        boxShadow: `inset 0 0 0 1px ${inputTokens.borderDefault}`,
        cursor: 'text',
        overflow: 'clip',
        transition: transitions.create(['box-shadow', 'background-color'], {
          duration: transitions.duration.shorter,
        }),
        [`${settled}:hover:not(:focus-within)`]: {
          boxShadow: `inset 0 0 0 1px ${inputTokens.borderHover}`,
        },
        [`${settled}:focus-within:not(:has(${FIELD}[data-pointer-focus]))`]: {
          boxShadow: `inset 0 0 0 2px ${inputTokens.borderFocus}`,
        },
        [`${settled}:has(${FIELD}:active)`]: {
          boxShadow: `inset 0 0 0 2px ${inputTokens.borderActive}`,
        },
        [`${settled}:has(${FIELD}[data-pointer-focus])`]: {
          boxShadow: `inset 0 0 0 2px ${inputTokens.borderActive}`,
        },
        [`&:has(${FIELD}[readonly]:not(:disabled))`]: {
          cursor: 'default',
          backgroundColor: inputTokens.bgReadOnly,
          boxShadow: `inset 0 0 0 1px ${inputTokens.borderDefault}`,
        },
        [`&:has(${FIELD}:disabled)`]: {
          cursor: 'not-allowed',
        },
        [`&:has(${FIELD}[aria-invalid="true"])`]: {
          boxShadow: `inset 0 0 0 2px ${inputTokens.borderError}`,
        },
      },
      inputWithLeftAdornment: {
        paddingLeft: 0,
      },
      inputWithRightAdornment: {
        paddingRight: 0,
      },
      prefixAdornment: {
        marginLeft: pxToRem(FIELD_INLINE_PADDING_PX),
      },
      suffixAdornment: {
        marginRight: pxToRem(FIELD_INLINE_PADDING_PX),
      },
      input: {
        flex: '1 1 auto',
        minWidth: 0,
        display: 'block',
        boxSizing: 'border-box',
        margin: 0,
        paddingLeft: pxToRem(FIELD_INLINE_PADDING_PX),
        paddingRight: pxToRem(FIELD_INLINE_PADDING_PX),
        appearance: 'none',
        border: 'none',
        borderRadius: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        color: palette.semantic.text.primary,
        fontFamily: 'Roobert, sans-serif',
        outline: 'none',
        cursor: 'inherit',
        ...handleBreakpoints(
          { theme: { breakpoints } },
          inputSize,
          (resolvedSize) => ({
            ...getInputBlockPadding(resolvedSize, pxToRem),
            ...getInputTypography(resolvedSize),
            ...(multiline ? getMultilineMinHeight(resolvedSize, pxToRem) : {}),
          })
        ),
        '&::placeholder': {
          color: palette.semantic.text.secondary,
          opacity: 1,
        },
      },
      textarea: {
        resize: 'vertical',
      },
      adornment: {
        ...type['18_regular'],
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(ADORNMENT_GAP_PX),
        color: palette.semantic.text.primary,
        pointerEvents: 'none',
        '& svg': {
          display: 'block',
          width: pxToRem(ADORNMENT_ICON_SIZE_PX),
          height: pxToRem(ADORNMENT_ICON_SIZE_PX),
          color: palette.semantic.icon.primary,
        },
        '& button, & a, & [role="button"]': {
          pointerEvents: 'auto',
        },
      },
      helper: {
        color: palette.semantic.text.primary,
        minHeight: pxToRem(20),
      },
    };
  }
);
